import { useCallback, useEffect, useRef } from "react";

/** Trailing-edge callback debounce. Missing/zero/negative/non-finite delay runs immediately. */
export default function useDebouncedCallback<TArgs extends unknown[]>(callback: (...args: TArgs) => void, delayMs = 0): (...args: TArgs) => void {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>();
  const callbackRef = useRef(callback);
  const delay = Number.isFinite(delayMs) ? Math.max(0, delayMs) : 0;

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(
    () => () => {
      if (timer.current !== undefined) clearTimeout(timer.current);
      timer.current = undefined;
    },
    [delay]
  );

  return useCallback(
    (...args: TArgs): void => {
      if (timer.current !== undefined) clearTimeout(timer.current);
      timer.current = undefined;
      if (delay === 0) {
        callbackRef.current(...args);
        return;
      }
      timer.current = setTimeout(() => {
        timer.current = undefined;
        callbackRef.current(...args);
      }, delay);
    },
    [delay]
  );
}
